import { EntitlementType } from '../entitlementTypes/entitlementType';
import { EntitlementHotlist } from './entitlements/models';
import {
  getActiveEntitlementTypes,
  getActivePassEntitlements,
  guestHasSharedVoucher,
  hasActivePass,
  isActive,
  isDeferred,
} from './entitlements/module';
import { getRequiredWaivers, hasValidWaiver } from './entitlements/waivers/module';
import { GuestDetails } from './guestDetails';
import { hasPhoto } from './guestPhoto/module';
import { getActiveHotlists } from './hotlist/module';
import ImmutableMap from '../language/ImmutableMap';
import { getResource } from '../language/resources/getResource';
import { ENTITLEMENT_STATUS } from './entitlements/constants';


const loadingOrHasNoPassMessage = (
  resources: Record<string, any> | undefined,
  passEntitlementId: number,
  guest: GuestDetails | undefined,
): string | undefined => {
  if (!guest) {
    return getResource(resources, 'FEATURE_DISABLING_LOADING_GUEST');
  }

  if (!hasActivePass(passEntitlementId, guest)) {
    return getResource(resources, 'FEATURE_DISABLING_NO_PASS');
  }

  return undefined;
};


/**
 * Whether or not the user should be allowed to print media for the given guest. If all is good, you'll get
 * back undefined. If the functionality should be disabled, you'll get back an error message you can show to the user.
 * @param passEntitlementId - The Entitlement Type ID for passes
 * @param guest - The guest in question
 * @param allEntitlementTypes - HashMap of all possible entitlement types
 */
export const passPrintDisabledMessage = (
  resources: Record<string, any> | undefined,
  passEntitlementId: number,
  guest: GuestDetails | undefined,
  allEntitlementTypes: ImmutableMap<EntitlementType> | undefined,
): string | undefined => {
  const hasGuestPhoto = hasPhoto(guest);
  const isInvalid     = loadingOrHasNoPassMessage(resources, passEntitlementId, guest);
  const entitlements = getActivePassEntitlements(passEntitlementId, guest).filter(x => x.entitlementStatusId === ENTITLEMENT_STATUS.ENABLED);
  const hasWaiver = hasValidWaiver(
    guest,
    getRequiredWaivers(getActiveEntitlementTypes(guest, allEntitlementTypes)),
  );

  if (isInvalid) {
    return isInvalid;
  }

  if (entitlements.length < 1) {
    return getResource(resources, 'FEATURE_DISABLING_NO_PASS');
  }

  if (!hasGuestPhoto && !hasWaiver) {
    return getResource(resources, 'NEED_PHOTO_AND_WAIVER');
  }

  if (!hasGuestPhoto) {
    return getResource(resources, 'NEED_PHOTO');
  }

  if (!hasWaiver) {
    return getResource(resources, 'NEED_WAIVER');
  }

  return undefined;
};


/**
 * Whether or not the user should be allowed to link an RFID code for the given guest. If all is good, you'll get
 * back undefined. If the functionality should be disabled, you'll get back an error message you can show to the user.
 * @param passEntitlementId - The Entitlement Type ID for passes
 * @param guest - The guest in question
 */
export const assocRfidDisabledMessage = loadingOrHasNoPassMessage;


/**
 * Whether or not the user should be allowed to sign a waiver for the given guest. If all is good, you'll get
 * back undefined. If the functionality should be disabled, you'll get back an error message you can show to the user.
 * @param passEntitlementId - The Entitlement Type ID for passes
 * @param guest - The guest in question
 */
export const signWaiverDisabledMessage = loadingOrHasNoPassMessage;


/**
 * Whether or not the user should be allowed to redeem an entitlement for the given guest. If all is good, you'll get
 * back undefined. If the functionality should be disabled, you'll get back an error message you can show to the user.
 * @param passEntitlementId - The Entitlement Type ID for passes
 * @param guest - The guest in question
 * @param hasSignedWaivers - Whether or not the guest has signed a waiver for an entitlement
 * @param entitlementHotlists - guest's history of hotlisting
 */
export const redeemEntitlementDisabledMessage = (
  resources: Record<string, any>,
  passEntitlementId: number,
  guest: GuestDetails | undefined,
  hasSignedWaivers: boolean,
  entitlementHotlists?: ImmutableMap<EntitlementHotlist[]>,
): string | undefined => {
  const loadingOrNoPass = loadingOrHasNoPassMessage(resources, passEntitlementId, guest);

  if (loadingOrNoPass) {
    return loadingOrNoPass;
  }

  const activePassEntitlements = getActivePassEntitlements(1, guest) ?? [];

  const deferred = activePassEntitlements.every(ent => isActive(ent) && isDeferred(ent));

  if (deferred) {
    return getResource(resources, 'FEATURE_DISABLING_DEFERRED');
  }

  const activeHotlists      = getActiveHotlists(entitlementHotlists);

  const everyHotlistFn = ent =>
    isActive(ent) &&
    activeHotlists
      .map(hotlist => hotlist.entitlementId)
      .includes(ent.entitlementId);

  const hotlisted = activePassEntitlements.every(everyHotlistFn);
  
  if (hotlisted) {
    return getResource(resources, 'FEATURE_DISABLING_HOTLISTED');
  }

  if(!hasSignedWaivers) {
    return getResource(resources, 'FEATURE_DISABLING_WAIVER');
  }

  return undefined;
};


export const redeemFriendsAndFamilyDisabledMessage = (
  resources: Record<string, any>,
  passEntitlementId: number,
  guest: GuestDetails | undefined,
  entitlementHotlists?: ImmutableMap<EntitlementHotlist[]>,
) : string | undefined => {

  if (!guest) {
    return getResource(resources, 'FEATURE_DISABLING_LOADING_GUEST');
  }

  const activePassEntitlements = getActivePassEntitlements(1, guest) ?? [];
  const hasFnfVouchers = guestHasSharedVoucher(guest);

  if (hasActivePass(passEntitlementId, guest)) {
    // Do not allow Fnf is guest is hotlisted and has Fnf vouchers
    if (hasFnfVouchers) {
      const activeHotlists = getActiveHotlists(entitlementHotlists);

      const everyHotlistFn = ent =>
        isActive(ent) &&
        activeHotlists
          .map(hotlist => hotlist.entitlementId)
          .includes(ent.entitlementId);
    
      const hotlisted = activePassEntitlements.every(everyHotlistFn);

      if (hotlisted) {
        return getResource(resources, 'FEATURE_DISABLING_HOTLISTED');
      }
    } else { // No Fnf vouchers and check for deferred pass
      const deferred = activePassEntitlements.every(ent => isDeferred(ent));

      if (deferred) {
        return getResource(resources, 'FEATURE_DISABLING_DEFERRED');
      }
    }
  } 

 // No Fnf entitlements
 if (!hasFnfVouchers) {
  return getResource(resources, 'FEATURE_DISABLING_NO_FNF');
}

 return undefined;
};


/**
 * Whether or not the user should be allowed to import the given guest. If all is good, you'll get back undefined. If
 * the functionality should be disabled, you'll get back an error message you can show to the user.
 * @param passEntitlementId - The Entitlement Type ID for passes
 * @param importSuccesses - The guests we know we've imported
 * @param guest - The guest in question
 */
export const importGuestDisabledMessage = (
  resources: Record<string, any>,
  passEntitlementId: number,
  importSuccesses: string[],
  guest: GuestDetails | undefined,
  importEnabled: boolean
): string | undefined => {
  if (!importEnabled) {
    return getResource(resources, 'FEATURE_DISABLING_NOT_AVAILABLE');
  }

  const error = loadingOrHasNoPassMessage(resources, passEntitlementId, guest);

  if (error) {
    return error;
  }

  if (importSuccesses.includes(guest!.masterGuestId)) {
    return getResource(resources, 'FEATURE_DISABLING_GUEST_IMPORTED');
  }

  return undefined;
};
